import { FieldLabel } from "../workspaces/shared/FieldLabel";
import { useEffect, useState } from "react";
import { userMessage } from "../workspaces/shared/userMessage";
import {
  acceptLegal,
  fetchPricing,
  formatPrice,
  openBillingPortal,
  refreshBilling,
  saveProfile,
  startCheckout,
  type AccountState,
  type HostedAccount,
  type PublicPricing,
} from "./session";
import { navigateTo } from "../public/PublicSite";
import { AuthLayout } from "./AuthLayout";
import { Badge, Callout } from "../workspaces/shared/ui";
import { Building2, CheckCircle2, CreditCard, Hammer, Home, KeyRound, Sprout, TrendingUp, type LucideIcon } from "lucide-react";

/**
 * FIRST LOGIN, BILLING AND AGREEMENTS (spec §86-2/11/14).
 *
 * Short on purpose. Onboarding asks four things that change what the person
 * sees first and nothing else; billing shows what the SERVER says about
 * access and hands off to Stripe; the agreement prompt appears only when the
 * documents changed since the person last accepted them.
 */

function Frame({ title, children, wide, lede, eyebrow }: { title: string; children: React.ReactNode; wide?: boolean; lede?: React.ReactNode; eyebrow?: string }) {
  return (
    <AuthLayout title={title} wide={wide} lede={lede} eyebrow={eyebrow}>
      {children}
    </AuthLayout>
  );
}

const EXPERIENCE: readonly { id: string; label: string; hint: string; icon: LucideIcon }[] = [
  { id: "new", label: "New to renovation", hint: "This is my first project, or nearly.", icon: Sprout },
  { id: "some", label: "Some experience", hint: "I've done a few projects.", icon: Hammer },
  { id: "experienced", label: "Experienced", hint: "Renovation is my business.", icon: Building2 },
];

const INTENT = [
  { id: "flip", label: "Flip", hint: "Buy, renovate and sell.", icon: TrendingUp },
  { id: "rental", label: "Rental", hint: "Buy, renovate and rent out.", icon: KeyRound },
  { id: "other", label: "Other renovation", hint: "My own home, or something else.", icon: Home },
] as const;

export function OnboardingScreen({ user, onDone }: { user: HostedAccount; onDone: (state: AccountState & { displayName?: string }) => void }) {
  useEffect(() => {
    document.title = "Welcome — Berrier Reno";
  }, []);
  const [name, setName] = useState(user.displayName && user.displayName !== user.email ? user.displayName : "");
  const [company, setCompany] = useState(user.profile?.companyName ?? "");
  const [experience, setExperience] = useState<string>(user.profile?.experienceLevel ?? "");
  const [intent, setIntent] = useState<string>(user.profile?.primaryIntent ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tile = (group: string, option: { id: string; label: string; hint: string; icon: LucideIcon }, value: string, set: (v: string) => void) => {
    const Icon = option.icon;
    return (
      <FieldLabel key={option.id} className={`choice-tile ${value === option.id ? "choice-tile-selected" : ""}`}>
        <input type="radio" name={group} checked={value === option.id} onChange={() => set(option.id)} />
        <span className="choice-tile-icon" aria-hidden="true">
          <Icon size={18} />
        </span>
        <strong>{option.label}</strong>
        <span>{option.hint}</span>
      </FieldLabel>
    );
  };
  const stepClass = (done: boolean) => `onboarding-step ${done ? "onboarding-step-done" : ""}`;

  return (
    <Frame
      title={`Welcome${name.trim() ? `, ${name.trim().split(/\s+/)[0]}` : ""}`}
      eyebrow="Three quick questions"
      lede="So Berrier Reno starts in the right place. You can change any of this later."
      wide
    >
      <form
        className="auth-form"
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          void saveProfile({ displayName: name, companyName: company, experienceLevel: experience, primaryIntent: intent, completeOnboarding: true })
            // The server saved the name; the signed-in person should see it now, not after a reload.
            .then((state) => onDone({ ...state, displayName: name.trim() }))
            .catch((err: unknown) => setError(userMessage(err, "That could not be saved.")))
            .finally(() => setBusy(false));
        }}
      >
        <div className="onboarding-steps">
          <div className={stepClass(name.trim() !== "")}>
            <span className="onboarding-step-number" aria-hidden="true">1</span>
            <div>
              <p className="onboarding-step-title">About you</p>
              <div className="onboarding-fields">
                <FieldLabel>
                  Your name
                  <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required autoFocus />
                </FieldLabel>
                <FieldLabel>
                  <span>
                    Company <span className="auth-hint">optional</span>
                  </span>
                  <input value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="organization" />
                </FieldLabel>
              </div>
            </div>
          </div>

          <fieldset className={stepClass(experience !== "")}>
            <span className="onboarding-step-number" aria-hidden="true">2</span>
            <div>
              <legend>How much renovation experience do you have?</legend>
              <div className="choice-tiles">{EXPERIENCE.map((option) => tile("experience", option, experience, setExperience))}</div>
            </div>
          </fieldset>

          <fieldset className={stepClass(intent !== "")}>
            <span className="onboarding-step-number" aria-hidden="true">3</span>
            <div>
              <legend>What will you mostly use Berrier Reno for?</legend>
              <div className="choice-tiles">{INTENT.map((option) => tile("intent", option, intent, setIntent))}</div>
            </div>
          </fieldset>
        </div>

        {error && <Callout tone="bad">{error}</Callout>}
        <div className="onboarding-actions">
          <span className="auth-hint">This only sets defaults. Every project can be a flip, a rental or anything else.</span>
          <button className="btn btn-primary" type="submit" disabled={busy || name.trim() === ""}>
            {busy ? "Saving…" : "Continue"}
          </button>
        </div>
      </form>
    </Frame>
  );
}

/** Shown when the documents changed since this person last accepted them. */
export function LegalAcceptScreen({ onAccepted }: { onAccepted: (state: AccountState) => void }) {
  useEffect(() => {
    document.title = "Review the terms — Berrier Reno";
  }, []);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Frame
      title="Please review our terms"
      lede={
        <>
          Read the <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a> and{" "}
          <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>, then accept them to continue.
        </>
      }
    >
      <div className="auth-form">
      <Callout tone="brand">
        In short: estimates are planning tools, not quotes; decisions about your property remain yours; your projects belong to you and are never sold.
      </Callout>
      <FieldLabel className="checkbox-label">
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        <span>I have read and accept the Terms of Service and Privacy Policy.</span>
      </FieldLabel>
      {error && (
        <div className="error-panel" role="alert">
          <p>{error}</p>
        </div>
      )}
      <button
        className="btn btn-primary"
        disabled={!checked || busy}
        onClick={() => {
          setBusy(true);
          void acceptLegal()
            .then(onAccepted)
            .catch((err: unknown) => setError(userMessage(err, "That could not be saved.")))
            .finally(() => setBusy(false));
        }}
      >
        Accept and continue
      </button>
      </div>
    </Frame>
  );
}

/**
 * WHAT ACCESS THIS PERSON HAS, IN WORDS (spec §88A). Read from the server's
 * access decision only. "No access" was shown to people who had access,
 * because a deployment that does not enforce billing leaves the source as
 * "none" while allowing everyone in — the words now follow allowed/enforced.
 */
export function accessSummary(access: AccountState["access"]): { label: string; tone: "good" | "brand" | "attention" | "bad" } {
  switch (access.source) {
    case "owner":
      return { label: "Owner", tone: "brand" };
    case "complimentary_beta":
      return { label: "Complimentary Early Access", tone: "good" };
    case "stripe_subscription":
      return access.warning ? { label: "Early Access subscription — needs attention", tone: "attention" } : { label: "Early Access subscription", tone: "good" };
    default:
      return access.allowed && !access.enforced ? { label: "Open Early Access", tone: "good" } : { label: "No access yet", tone: "bad" };
  }
}

/**
 * BILLING (spec §86-11). Everything shown comes from the server; after
 * returning from Stripe Checkout it re-asks the server (which re-reads
 * Stripe) a few times, because the webhook and the browser race.
 */
export function BillingScreen({
  state,
  onState,
  onContinue,
  onSignOut,
  embedded,
}: {
  state: AccountState;
  onState: (state: AccountState) => void;
  onContinue?: () => void;
  onSignOut?: () => void;
  /** Inside the app shell (managing) rather than blocking access. */
  embedded?: boolean;
}) {
  const [pricing, setPricing] = useState<PublicPricing | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const returned = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("checkout") : null;
  const [waiting, setWaiting] = useState(returned === "success" && !state.access.allowed);

  useEffect(() => {
    void fetchPricing().then(setPricing);
  }, []);

  useEffect(() => {
    if (!waiting) return;
    let tries = 0;
    let alive = true;
    const tick = async () => {
      tries += 1;
      try {
        const next = await refreshBilling();
        if (!alive) return;
        onState(next);
        if (next.access.allowed || tries >= 10) {
          setWaiting(false);
          return;
        }
      } catch {
        /* keep trying a little longer */
      }
      if (alive) setTimeout(() => void tick(), 2000);
    };
    void tick();
    return () => {
      alive = false;
    };
  }, [waiting, onState]);

  const go = (action: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    void action()
      .then((url) => {
        window.location.href = url;
      })
      .catch((err: unknown) => {
        setError(userMessage(err, "That did not work."));
        setBusy(false);
      });
  };

  const price = pricing ? formatPrice(pricing) : null;
  const access = state.access;
  const summary = accessSummary(access);
  const needsPlan = !access.allowed || (access.source === "none" && access.enforced);
  const content = (
    <div className="billing-body">
      {waiting && (
        <Callout tone="brand">
          <strong>Confirming your payment with Stripe…</strong> This usually takes a few seconds.
        </Callout>
      )}
      {returned === "cancelled" && !access.allowed && <Callout tone="attention">Checkout was cancelled — nothing was charged.</Callout>}

      <div className="access-card">
        <span className={`access-card-icon access-card-${summary.tone}`} aria-hidden="true">
          {access.allowed ? <CheckCircle2 size={22} /> : <CreditCard size={22} />}
        </span>
        <div>
          <span className="access-card-label">Your access</span>
          <strong>{summary.label}</strong>
          <span className="muted">
            {access.source === "none" && access.allowed && !access.enforced
              ? "Billing is not switched on yet, so every account has full access during Early Access."
              : access.explanation}
          </span>
        </div>
        {access.source === "stripe_subscription" && (
          <div className="access-card-side">
            <span className="access-card-label">{access.warning === "cancels_at_period_end" ? "Ends" : "Renews"}</span>
            <strong>{access.currentPeriodEnd ? new Date(access.currentPeriodEnd).toLocaleDateString() : "—"}</strong>
          </div>
        )}
      </div>

      {needsPlan &&
        (state.billing.configured ? (
          <div className="plan-card">
            <div>
              <Badge tone="brand">Early Access</Badge>
              <h2>{pricing?.productName ?? "Berrier Reno Early Access"}</h2>
              <p className="plan-price">{price ?? "—"}</p>
              <p className="muted">Everything in Berrier Reno. Cancel any time; your projects are always kept.</p>
            </div>
            <button className="btn btn-primary" disabled={busy} onClick={() => go(startCheckout)}>
              {busy ? "Opening Stripe…" : "Start Early Access"}
            </button>
            <p className="auth-hint">
              Payment is handled by Stripe. By subscribing you agree to the <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a>.
            </p>
          </div>
        ) : (
          <Callout tone="attention">
            Paid Early Access is not open yet. If you were invited, the administrator can turn on access for your account — reply to your invitation email or use
            Send feedback.
          </Callout>
        ))}

      {error && <Callout tone="bad">{error}</Callout>}
      <div className="billing-actions">
        {state.billing.canManage && (
          <button className="btn" disabled={busy} onClick={() => go(openBillingPortal)}>
            Manage billing, invoices or cancellation
          </button>
        )}
        {access.allowed && onContinue && (
          <button className="btn btn-primary" onClick={onContinue}>
            Continue to Berrier Reno
          </button>
        )}
        {!embedded && onSignOut && (
          <button className="btn btn-ghost" onClick={onSignOut}>
            Sign out
          </button>
        )}
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className="settings-page">
        <PageHeaderLite title="Billing" lede="Your plan and how you pay for it." />
        <section className="panel">{content}</section>
      </div>
    );
  }
  return (
    <Frame title={access.allowed ? "Your access" : "Start Early Access"} lede={access.allowed ? undefined : "Choose a plan to open your projects."}>
      {content}
    </Frame>
  );
}

function PageHeaderLite({ title, lede }: { title: string; lede: string }) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        <p className="muted page-lede">{lede}</p>
      </div>
    </div>
  );
}

/** A one-line banner inside the app when access has a warning attached. */
export function AccessWarningBanner({ state }: { state: AccountState }) {
  if (!state.access.warning) return null;
  return (
    <div className="access-warning no-print" role="status">
      <span>{state.access.explanation}</span>
      <button className="btn btn-small" onClick={() => navigateTo("/billing")}>
        {state.access.warning === "payment_past_due" ? "Update payment method" : "Manage subscription"}
      </button>
    </div>
  );
}
